import {
  Injectable
} from '@angular/core';
import {
  Actions,
  Effect,
  ofType
} from '@ngrx/effects';
import {
  Store,
  Action
} from '@ngrx/store';
import * as fromRoot from 'src/store';
import * as fromActions from './workspace.actions';
import {
  map,
  catchError,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';
import {
  ApiService,
  GetTimeSheetsRequest,
} from 'src/app/services/api.service';
import {
  MsAdalAngular6Service
} from 'microsoft-adal-angular6';
import {
  AppState
} from 'src/store';
import * as moment from 'moment';
import {
  TimeSheet,
  formatTimesheetForRequest,
  TimeSummaryRow,
  SearchDateOptions,
  StaffDTO
} from './workspace.model';
import {
  Observable, of
} from 'rxjs';
import {
  cloneDeep
} from 'lodash';
import {
  getCurrentPayPeriod
} from './workspace.reducer';

@Injectable()
export class WorkspaceEffects {

  constructor(
    private actions$: Actions,
    private store$: Store < fromRoot.AppState > ,
    private apiService: ApiService,
    private adal: MsAdalAngular6Service
  ) {}

  @Effect()
  setPayPeriods$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SET_PAY_PERIODS),
    map(() => {
      return new fromActions.SelectPayPeriod();
    })
  );

  @Effect()
  deleteTimesheet$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.DELETE_TIMESHEET),
    switchMap((action: fromActions.DeleteTimesheet) => {
      const sheet = cloneDeep(action.payload);
      if (!sheet.timeSummaryIdx) {
        return this.apiService.deleteTimesheet(sheet.timeSheetIdx).pipe(
          switchMap((response: any) => [new fromActions.DeleteTimesheetSuccess(response), new fromActions.LoadSums()]),
          catchError(() => [new fromActions.SendMessage({
            type: 'danger',
            content: 'Error when deleting entry'
          })])
        );
      }
      return this.apiService.undoSummaryRow({timeSummaryIdx: sheet.timeSummaryIdx}).pipe(
        switchMap((response: any) => {
          sheet.timeSummaryIdx = null;
          return this.apiService.deleteTimesheet(sheet.timeSheetIdx).pipe(
          switchMap((r: any) => [new fromActions.DeleteTimesheetSuccess(r), new fromActions.Search()]),
          catchError(() => [new fromActions.SendMessage({
            type: 'danger',
            content: 'Error when deleting entry'
          })])
        );
      }),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error When Undoing Summary Submission'
        })])
      );
    })
  );

  @Effect()
  loadCategories$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_CATEGORIES),
    switchMap((action: fromActions.LoadCategories) => {
      return this.apiService.getCategories().pipe(
        switchMap((response) => [new fromActions.SetCategories(response)]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error when retrieving categories'
        })])
      );
    })
  );

  @Effect()
  addCategory$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.ADD_CATEGORY),
    switchMap((action: fromActions.AddCategory) => {
      return this.apiService.addCategory(action.payload).pipe(
        switchMap((response) => [new fromActions.SendMessage({
          type: 'success',
          content: 'Category Successfully added'
        }), new fromActions.LoadCategories()]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error when saving category'
        })])
      );
    })
  );

  @Effect()
  addDescription$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.ADD_DESCRIPTION),
    switchMap((action: fromActions.AddDescription) => {
      return this.apiService.addDescription(action.payload).pipe(
        switchMap((response) => [new fromActions.SendMessage({
          type: 'success',
          content: 'Description Successfully added'
        }), new fromActions.LoadDescriptions()]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error when saving description'
        })])
      );
    })
  );

  @Effect()
  saveTimesheet$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SAVE_TIMESHEET),
    switchMap((action: fromActions.SaveTimesheet) => {
      let request: Observable < TimeSheet > ;
      if (action.payload.timeSheetIdx) {
        request = this.apiService.saveTimesheet(formatTimesheetForRequest(action.payload));
      } else {
        const req = Object.assign({}, action.payload);
        delete req.timeSheetIdx;
        request = this.apiService.addTimesheet(formatTimesheetForRequest(req));
      }
      return request.pipe(
        switchMap((response: any) => [new fromActions.SaveTimesheetSuccess(response), new fromActions.Search()]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error when inserting entry'
        })])
      );
    }),

  );

  @Effect()
  saveTimeSheetSuccess$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SAVE_TIMESHEET_SUCCESS, fromActions.WorkspaceActionTypes.DELETE_TIMESHEET_SUCCESS),
    switchMap((action: {
      type: fromActions.WorkspaceActionTypes,
      payload: any
    }) => {
      return [new fromActions.SendMessage({
        type: 'success',
        content: action.type
      }), new fromActions.SetLoading(false)];
    })
  );

  @Effect()
  loadUnsubmittedSummary$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_UNSUBMITTED_SUMMARY),
    withLatestFrom(this.store$),
    switchMap(([action, store]) => {
      let clientID = 0;
      if (store.workspace.searchByOptions.client) {
        clientID = store.workspace.searchByOptions.client.clientID;
      }
      return this.apiService.getUnsubmittedSummary({
        staffID: store.workspace.staff.staffID,
        clientID,
        PayPeriodStarting: moment(store.workspace.searchByOptions.payPeriod.payPeriodStarting).format('MM/D/YYYY'),
        PayPeriodEnding: moment(store.workspace.searchByOptions.payPeriod.payPeriodEnding).format('MM/D/YYYY'),
        submittedFlag: 0
      }).pipe(
        switchMap((response: TimeSummaryRow[]) => [new fromActions.SetUnsubmittedSummaryRows(response), new fromActions.SetLoading(false)]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error Loading Summary'
        }), new fromActions.SetLoading(false)])
      );
    })
  );

  @Effect()
  submitSummaryRow$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SUBMIT_SUMMARY_ROW),
    switchMap((action: fromActions.SubmitSummaryRow) => {
      const opts = {
        staffID: action.payload.staffID,
        clientID: action.payload.clientID,
        PayPeriodStarting: moment(action.payload.payPeriodStarting).format('MM/D/YYYY'),
        PayPeriodEnding: moment(action.payload.payPeriodEnding).format('MM/D/YYYY'),
        submittedDate: moment().format('MM/D/YYYY'),
        totalHoursBillable: action.payload.hoursBillable,
      };
      return this.apiService.submitSummaryRow(opts).pipe(
        switchMap((response: TimeSummaryRow[]) => [new fromActions.SendMessage({
            type: 'success',
            content: 'Successfully Submitted Timesheet'
          }),
          new fromActions.Search(), new fromActions.SetLoading(false)
        ]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error Submitting Timesheet'
        }), new fromActions.SetLoading(false)])
      );
    })
  );


  @Effect()
  search$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_TIMESHEETS, fromActions.WorkspaceActionTypes.SEARCH),
    withLatestFrom(this.store$),
    switchMap(([action, state]: [Action, AppState]) => {
      const searchOpts = state.workspace.searchByOptions;
      let start = searchOpts.payPeriod.payPeriodStarting;
      let end = searchOpts.payPeriod.payPeriodEnding;
      const clientID = searchOpts.client ? searchOpts.client.clientID : 0;
      if (searchOpts.dateOption === SearchDateOptions.DateRange) {
        start = searchOpts.dateRange.DateStarting;
        end = searchOpts.dateRange.DateEnding;
      }
      const opts: GetTimeSheetsRequest = {
        StaffID: searchOpts.staff.staffID,
        StartDateWorked: moment(start).isValid() ? moment(start).format('MM/D/YYYY') : '',
        EndDateWorked: moment(end).isValid() ? moment(end).format('MM/D/YYYY') : '',
        ClientID: clientID
      };

      return this.apiService.getTimeSheets(opts).pipe(
        switchMap((data: TimeSheet[]) => {
          return [new fromActions.SetTimesheets(data), new fromActions.LoadSummaries()];
        }),
        catchError((req) => [new fromActions.SendMessage({
          type: 'danger',
          content: 'There was an issue with your search terms. Please review and try again.'
        }), new fromActions.SetLoading(false)])
      );
    })
  );

  @Effect()
  loadSummaries$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_SUMMARIES),
    withLatestFrom(this.store$),
    switchMap(([action, state]: [Action, AppState]) => {
      return this.apiService.getSummary({
        staffID: state.workspace.searchByOptions.staff.staffID,
        clientID: state.workspace.searchByOptions.client ? state.workspace.searchByOptions.client.clientID : 0,
        PayPeriodStartDate: moment(state.workspace.searchByOptions.payPeriod.payPeriodStarting).format('MM/D/YYYY'),
        PayPeriodEndDate: moment(state.workspace.searchByOptions.payPeriod.payPeriodEnding).format('MM/D/YYYY'),
        submittedFlag: 2,
      }).pipe(
        switchMap((response) => {
          const ret: Action[] = [
            new fromActions.SetSummaryRows(response),
            new fromActions.SetLoading(false)
          ];
          const hasPastUnsubmittedSummaries = response.some
          (summary => {
            return !summary.timeSummaryIdx && moment(summary.payPeriodEnding).isBefore(moment(), 'd');
          });
          if (hasPastUnsubmittedSummaries) {
            ret.push(new fromActions.AddAlert({
              content: `You have unsubmitted time in at least one past time period. Please submit your time as soon as possible.`,
              type: 'warning',
              title: 'Unsubmitted Time Summary',
              id: 'unsubmitted-time-summary'
            }));
          } else {
            ret.push(new fromActions.RemoveAlert('unsubmitted-time-summary'));
          }
          return ret;
        }),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'There was an error loading summaries'
        })]),
      );
    })
  );

  @Effect()
  undoSummaryRow$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.UNDO_SUMMARY_ROW),
    switchMap((action: fromActions.UndoSummaryRow) => {
      return this.apiService.undoSummaryRow({timeSummaryIdx: action.payload}).pipe(
        switchMap(() => [
          new fromActions.SendMessage({content: 'Summary Successfully Unsubmitted', type: 'success'}),
          new fromActions.Search(),
          new fromActions.SetLoading(false), new fromActions.SetLoading(false)]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'There was an error undoing this summary row'
        }), new fromActions.SetLoading(false), new fromActions.SetLoading(false)]),
      );
    })
  );

  @Effect()
  loadPTO$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_PTO),
    withLatestFrom(this.store$),
    switchMap(([action, state]: [fromActions.LoadPTO, AppState]) => {

      const selectedStaffID = state.workspace.selectedStaffID;

      return this.apiService.getPaidTimeOff({
        staffID: selectedStaffID,
        year: new Date().getFullYear()
      }).pipe(
        map((pto) => new fromActions.SetPTO(pto)),
        catchError(() => of(new fromActions.SendMessage({
          type: 'danger',
          content: 'There was an error loading paid time off'
        }))
      )
    )}
  ));

  @Effect()
  setTimesheets$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SET_TIMESHEETS),
    map(() => new fromActions.LoadSums())
  );

  @Effect()
  selectStaff$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SELECT_STAFF),
    withLatestFrom(this.store$),
    switchMap(([action, state]: [fromActions.SelectStaff, AppState]) => {
      const selectedStaffID = action.payload.staffID;
      return [
       new fromActions.SetSelectedStaffID(selectedStaffID),
        new fromActions.LoadTimesheets()
      ];
    }),
  );

  @Effect()
  loadStaff$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_STAFF),
    switchMap(() => {
      return this.apiService.getStaff().pipe(
        switchMap(data => {
          const currentUser = data.find(users => users.isCurrentUser);
          if (!currentUser.active || (currentUser.emailAddress !== this.adal.LoggedInUserEmail)) {
            this.adal.logout();
            return;
          }
          const list: StaffDTO | any[] = [{
            staffID: 0,
            staffName: 'All'
          }];
          list.push(...data);
          return [
            new fromActions.SelectStaff(currentUser),
            new fromActions.SetStaffList(list),
            new fromActions.LoadStaffCodes(),
            new fromActions.LoadAlerts(),
            new fromActions.LoadDescriptions(),
            new fromActions.LoadPTO()
          ];
        }),
        catchError(error => {
          return [];
        }),
      );
    })
  );

  @Effect()
  loadStaffCodes$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_STAFF_CODES),
    withLatestFrom(this.store$),
    switchMap(([action, state]: [fromActions.LoadStaffCodes, AppState]) => {

      return this.apiService.getStaffCodes()
        .pipe(
        map((staffCodes) => new fromActions.SetStaffCodes(staffCodes)),
        catchError(() => of(new fromActions.SendMessage({
          type: 'danger',
          content: 'There was an error loading staff codes'
        }))
      )
    )}
  ));

  @Effect()
  loadPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_PAGE_SIZE),
    map((action: fromActions.LoadPageSize) => {
      const size = localStorage.getItem('tsp-tt-page-size');
      if (Number(size)) {
        return new fromActions.SetPageSize(Number(size));
      }
      return new fromActions.SetPageSize('all');
    })
  );

  @Effect({
    dispatch: false
  })
  setPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SET_PAGE_SIZE),
    map((action: fromActions.SetPageSize) => {
      localStorage.setItem('tsp-tt-page-size', String(action.payload));
    })
  );

  @Effect()
  loadSummaryPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_SUMMARIES_PAGE_SIZE),
    map((action: fromActions.LoadSummariesPageSize) => {
      const size = localStorage.getItem('tsp-tt-summaries-page-size');
      if (Number(size)) {
        return new fromActions.SetSummariesPageSize(Number(size));
      }
      return new fromActions.SetSummariesPageSize('all');
    })
  );

  @Effect({
    dispatch: false
  })
  setSummaryPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SET_SUMMARIES_PAGE_SIZE),
    map((action: fromActions.SetSummariesPageSize) => {
      localStorage.setItem('tsp-tt-summaries-page-size', String(action.payload));
    })
  );

  @Effect({
    dispatch: false
  })
  setCategoriesPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SET_CATEGORIES_PAGE_SIZE),
    map((action: fromActions.SetCategoriesPageSize) => {
      localStorage.setItem('tsp-tt-categories-page-size', String(action.payload));
    })
  );

  @Effect()
  loadCategoriesPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_CATEGORIES_PAGE_SIZE),
    map((action: fromActions.LoadCategoriesPageSize) => {
      const size = localStorage.getItem('tsp-tt-categories-page-size');
      if (Number(size)) {
        return new fromActions.SetCategoriesPageSize(Number(size));
      }
      return new fromActions.SetCategoriesPageSize('all');
    })
  );


  @Effect({
    dispatch: false
  })
  setDescriptionsPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SET_DESCRIPTIONS_PAGE_SIZE),
    map((action: fromActions.SetDescriptionsPageSize) => {
      localStorage.setItem('tsp-tt-descriptions-page-size', String(action.payload));
    })
  );

  @Effect()
  loadDescriptionsPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_DESCRIPTIONS_PAGE_SIZE),
    map((action: fromActions.LoadDescriptionsPageSize) => {
      const size = localStorage.getItem('tsp-tt-descriptions-page-size');
      if (Number(size)) {
        return new fromActions.SetDescriptionsPageSize(Number(size));
      }
      return new fromActions.SetDescriptionsPageSize('all');
    })
  );

  @Effect()
  loadClientsPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_CLIENTS_PAGE_SIZE),
    map((action: fromActions.LoadClientsPageSize) => {
      const size = localStorage.getItem('tsp-tt-clients-page-size');
      if (Number(size)) {
        return new fromActions.SetClientsPageSize(Number(size));
      }
      return new fromActions.SetClientsPageSize('all');
    })
  );


  @Effect({
    dispatch: false
  })
  setClientsPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SET_CLIENTS_PAGE_SIZE),
    map((action: fromActions.SetClientsPageSize) => {
      localStorage.setItem('tsp-tt-clients-page-size', String(action.payload));
    })
  );

  @Effect()
  loadStaffPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_CLIENTS_PAGE_SIZE),
    map((action: fromActions.LoadStaffPageSize) => {
      const size = localStorage.getItem('tsp-tt-staff-page-size');
      if (Number(size)) {
        return new fromActions.SetStaffPageSize(Number(size));
      }
      return new fromActions.SetStaffPageSize('all');
    })
  );


  @Effect({
    dispatch: false
  })
  setStaffPageSize$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.SET_CLIENTS_PAGE_SIZE),
    map((action: fromActions.SetStaffPageSize) => {
      localStorage.setItem('tsp-tt-staff-page-size', String(action.payload));
    })
  );

  @Effect()
  addClient$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.ADD_CLIENT),
    switchMap((action: fromActions.AddClient) => {
      return this.apiService.addClient(action.payload).pipe(
        switchMap((response) => [new fromActions.SendMessage({
          type: 'success',
          content: 'Client Successfully added'
        }), new fromActions.LoadClients()]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error when saving client'
        })])
      );
    })
  );

  @Effect()
  deleteClient$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.DELETE_CLIENT),
    switchMap((action: fromActions.DeleteClient) => {
      return this.apiService.deleteClient(action.payload).pipe(
        switchMap((response) => [new fromActions.SendMessage({
          type: 'success',
          content: 'Client Successfully Deleted'
        }), new fromActions.LoadClients()]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error when deleting client'
        })])
      );
    })
  );

  @Effect()
  updateClient$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.UPDATE_CLIENT),
    switchMap((action: fromActions.UpdateClient) => {
      return this.apiService.updateClient(action.payload).pipe(
        switchMap((response) => [new fromActions.SendMessage({
          type: 'success',
          content: 'Client Successfully Updated'
        }), new fromActions.LoadClients()]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error when updating client'
        })])
      );
    })
  );

  @Effect()
  addStaff$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.ADD_STAFF),
    switchMap((action: fromActions.AddStaff) => {
      return this.apiService.addStaff(action.payload).pipe(
        switchMap((response) => [new fromActions.SendMessage({
          type: 'success',
          content: 'Staff Successfully added'
        }), new fromActions.LoadStaffs()]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error when saving staff'
        })])
      );
    })
  );

  @Effect()
  deleteStaff$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.DELETE_STAFF),
    switchMap((action: fromActions.DeleteStaff) => {
      return this.apiService.deleteStaff(action.payload).pipe(
        switchMap((response) => [new fromActions.SendMessage({
          type: 'success',
          content: 'Staff Successfully Deleted'
        }), new fromActions.LoadStaffs()]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error when deleting staff'
        })])
      );
    })
  );

  @Effect()
  updateStaff$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.UPDATE_STAFF),
    switchMap((action: fromActions.UpdateStaff) => {
      return this.apiService.updateStaff(action.payload).pipe(
        switchMap((response) => [new fromActions.SendMessage({
          type: 'success',
          content: 'Staff Successfully Updated'
        }), new fromActions.LoadStaffs()]),
        catchError(() => [new fromActions.SendMessage({
          type: 'danger',
          content: 'Error when updating staff'
        })])
      );
    })
  );

  @Effect()
  loadAlerts = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_ALERTS),
    withLatestFrom(this.store$),
    switchMap(([action, state]: [fromActions.LoadAlerts, AppState]) => {
      const currentPayPeriod = getCurrentPayPeriod(state.workspace.payPeriods);
      const lastDay = moment(currentPayPeriod.payPeriodEnding);
      const weekday = lastDay.get('weekday');
      const isSunday = !weekday;
      const isSaturday = lastDay.get('weekday') === 6;
      if (isSunday || isSaturday) {
        const dayOffset = isSunday ? 2 : 1;
        lastDay.set('date', lastDay.get('date') - dayOffset);
      }
      const today = moment();
      if (!today.isSameOrAfter(lastDay)) {
        return [];
      }
      // let's check for unsubmitted records
      return this.apiService.getTimeSheets({
        StaffID: state.workspace.staff.staffID,
        ClientID: 0,
        StartDateWorked: moment(currentPayPeriod.payPeriodStarting).format('MM/D/YYYY'),
        EndDateWorked: moment(currentPayPeriod.payPeriodEnding).format('MM/D/YYYY')
      }).pipe(
        switchMap((results: TimeSheet[]) => {
          const hasUnsubmittedSheets = results.some(sheet => !sheet.timeSummaryIdx);
          if (hasUnsubmittedSheets) {
            return [new fromActions.AddAlert({
              content: `The current pay period (${currentPayPeriod.text}) ends today.  Please do not forget to finalize and submit your time by the end of the day.  Thanks.`,
              type: 'danger',
              title: 'End of Period Alert',
              id: 'unsubmitted-time'
            })];
          } else {
            return [new fromActions.RemoveAlert('unsubmitted-time')];
          }
        })
      );
    })
  );

  @Effect()
  loadPayPeriods$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_PAY_PERIODS),
    switchMap(() => {
      return this.apiService.getPayPeriods().pipe(
        switchMap(response => {
          return [
            new fromActions.SetPayPeriods(response),
            new fromActions.LoadStaff(),
          ];
        }),
        catchError(error => {
          return [];
        }),
      );
    })
  );

  @Effect()
  loadDescriptions = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_DESCRIPTIONS),
    withLatestFrom(this.store$),
    switchMap(([action, state]) => {
      if (!state.workspace.staff) {
        return [];
      }
      return this.apiService.getDescriptions(state.workspace.staff.isCurrentUserAdmin).pipe(
        map(res => {
          return new fromActions.SetDescriptions(res);
        }),
        catchError(error => {
          return [new fromActions.SendMessage({
            content: 'There was a problem loading descriptions',
            type: 'danger',
          })];
        })
      );
    })
  );

  @Effect()
  updateDescription$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.UPDATE_DESCRIPTION),
    switchMap((action: fromActions.UpdateDescription) => {
      return this.apiService.updateDescription(action.payload).pipe(
        switchMap(response => {
          return [new fromActions.SendMessage({content: 'Description Updated', type: 'success'}), new fromActions.LoadDescriptions()];
        }),
        catchError(() => [new fromActions.SendMessage({content: 'Error updating description', type: 'danger'})])
      );
    })
  );

  @Effect()
  loadClients$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_CLIENTS),
    switchMap(() => {
      return this.apiService.getClients().pipe(
        switchMap(response => {
          return [
            new fromActions.SetClients(response),
            new fromActions.LoadDescriptions()
          ];
        }),
        catchError(error => {
          return [];
        }),
      );
    })
  );

  @Effect()
  loadStaffs$ = this.actions$.pipe(
    ofType(fromActions.WorkspaceActionTypes.LOAD_STAFFS),
    switchMap(() => {
      return this.apiService.getStaff().pipe(
        switchMap(response => {
          return [
            new fromActions.SetStaffs(response)
          ];
        }),
        catchError(error => {
          return [];
        }),
      );
    })
  );
}
